import { get, post, put, del, patch, ApiError, ApiErrorType } from './api';
import { SimpleCompetitor, CompetitionAnalysis } from '../types/competitors';

// Google Places API address components
export interface AddressComponents {
  street_number?: string;
  route?: string;
  neighborhood?: string;
  city?: string;
  county?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

// Google Places API location details
export interface LocationDetails {
  place_id: string;
  formatted_address: string;
  address_components: AddressComponents;
  latitude: number;
  longitude: number;
}

// Data models
export interface Location {
  id?: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

export interface ProjectMember {
  userId?: string;
  email: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  status: 'active' | 'pending';
}

export interface SocialMediaPlatform {
  platform: string;
  url: string;
}

export interface SocialMedia {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  linkedin?: string;
  youtube?: string;
  tiktok?: string;
  other?: SocialMediaPlatform[];
}

export interface LocalFacebookGroup {
  group_name: string;
  url: string;
  description?: string;
}

export interface Project {
  id: string;
  name: string;
  location?: Location;
  address?: string;
  status: 'New' | 'Analyzed' | 'Monitoring';
  lastUpdated?: string;
  createdAt?: string;
  created_at?: string;
  updated_at?: string;
  userId?: string;
  createdBy?: string;
  members?: ProjectMember[];
  description?: string;
  propertyDetails?: {
    squareFootage?: number;
    ceilingHeight?: number;
  };
  dealTerms?: string;
  businessType?: 'Entertainment' | 'Restaurant' | 'Daycare' | 'Printing Services' | 'Storage' | 'Other';
  industry?: string;
  social_media?: SocialMedia;
  local_facebook_groups?: LocalFacebookGroup[];
  documents?: Array<{
    id: string;
    name: string;
    type: string;
    size: number;
    url: string;
    uploadDate: string;
  }>;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  location_details?: LocationDetails;
  metadata?: any;
}

// Re-export SimpleCompetitor for backward compatibility
export type { SimpleCompetitor as Competitor };

// Project operations
export const getProjects = async (): Promise<Project[]> => {
  try {
    return await get<Project[]>('/api/v1/projects');
  } catch (error) {
    console.error('Failed to fetch projects:', error);
    throw error;
  }
};

export const getProject = async (id: string): Promise<Project> => {
  try {
    return await get<Project>(`/api/v1/projects/${id}`);
  } catch (error) {
    console.error(`Failed to fetch project ${id}:`, error);
    throw error;
  }
};

export interface CreateProjectData {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  location_details?: LocationDetails;
  status?: 'New' | 'Analyzed' | 'Monitoring';
  industry?: string;
  businessType?: 'Entertainment' | 'Restaurant' | 'Daycare' | 'Printing Services' | 'Storage' | 'Other';
  social_media?: SocialMedia;
  local_facebook_groups?: LocalFacebookGroup[];
}

export const createProject = async (projectData: CreateProjectData): Promise<Project> => {
  try {
    return await post<Project>('/api/v1/projects', projectData);
  } catch (error) {
    console.error('Failed to create project:', error);
    throw error;
  }
};

export const updateProject = async (id: string, projectData: Partial<Project>): Promise<Project> => {
  try {
    return await put<Project>(`/api/v1/projects/${id}`, projectData);
  } catch (error) {
    console.error(`Failed to update project ${id}:`, error);
    throw error;
  }
};

export const deleteProject = async (id: string): Promise<void> => {
  try {
    await del<void>(`/api/v1/projects/${id}`);
  } catch (error) {
    console.error(`Failed to delete project ${id}:`, error);
    throw error;
  }
};

// Project member operations
export interface AddMemberData {
  email: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
}

export interface UpdateMemberRoleData {
  role: 'owner' | 'admin' | 'editor' | 'viewer';
}

export const getProjectMembers = async (projectId: string): Promise<ProjectMember[]> => {
  try {
    return await get<ProjectMember[]>(`/api/v1/projects/${projectId}/members`);
  } catch (error) {
    console.error(`Failed to fetch members for project ${projectId}:`, error);
    throw error;
  }
};

export const addProjectMember = async (projectId: string, memberData: AddMemberData): Promise<Project> => {
  try {
    return await post<Project>(`/api/v1/projects/${projectId}/members`, memberData);
  } catch (error) {
    console.error(`Failed to add member to project ${projectId}:`, error);
    throw error;
  }
};

export const updateMemberRole = async (projectId: string, email: string, roleData: UpdateMemberRoleData): Promise<Project> => {
  try {
    return await patch<Project>(`/api/v1/projects/${projectId}/members/${email}/role`, roleData);
  } catch (error) {
    console.error(`Failed to update member role in project ${projectId}:`, error);
    throw error;
  }
};

export const removeProjectMember = async (projectId: string, email: string): Promise<Project> => {
  try {
    return await del<Project>(`/api/v1/projects/${projectId}/members/${email}`);
  } catch (error) {
    console.error(`Failed to remove member from project ${projectId}:`, error);
    throw error;
  }
};

// Competition analysis
export const getCompetitionAnalysis = async (projectId: string): Promise<CompetitionAnalysis> => {
  try {
    // Try to get competition data
    return await get<CompetitionAnalysis>(`/api/v1/projects/${projectId}/competitors`);
  } catch (error) {
    // Check if it's a 404 error, which is expected for new projects
    if (error instanceof ApiError && error.type === ApiErrorType.NOT_FOUND) {
      // For new projects with no competition data, return an empty analysis
      // This prevents the 404 error from showing in the console
      console.log(`No competition analysis available yet for project ${projectId} (expected for new projects)`);
      return {
        projectId,
        competitors: []
      };
    } else {
      console.error(`Failed to fetch competition analysis for project ${projectId}:`, error);
      throw error;
    }
  }
};

// Location operations
export const getLocations = async (): Promise<Location[]> => {
  try {
    return await get<Location[]>('/api/v1/locations');
  } catch (error) {
    console.error('Failed to fetch locations:', error);
    throw error;
  }
};

export const getLocation = async (id: string): Promise<Location> => {
  try {
    return await get<Location>(`/api/v1/locations/${id}`);
  } catch (error) {
    console.error(`Failed to fetch location ${id}:`, error);
    throw error;
  }
};

export const createLocation = async (locationData: Omit<Location, 'id'>): Promise<Location> => {
  try {
    return await post<Location>('/api/v1/locations', locationData);
  } catch (error) {
    console.error('Failed to create location:', error);
    throw error;
  }
};

export const updateLocation = async (id: string, locationData: Partial<Location>): Promise<Location> => {
  try {
    return await put<Location>(`/api/v1/locations/${id}`, locationData);
  } catch (error) {
    console.error(`Failed to update location ${id}:`, error);
    throw error;
  }
};

export const deleteLocation = async (id: string): Promise<void> => {
  try {
    await del<void>(`/api/v1/locations/${id}`);
  } catch (error) {
    console.error(`Failed to delete location ${id}:`, error);
    throw error;
  }
};
